import { ProductDocumentDto } from 'api/documents';
import { Button } from 'components/ui/Button';
import { CardTableDisplay } from 'components/display/Card/CardTable/CardTableDisplay';
import { CardTableColumn } from 'components/display/Card/CardTable/types';
import { Sizes } from 'enums/sizes';
import { usePanel } from 'hooks/usePanel';
import { Clock, FileText, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { documentsAPI } from 'services/swagger-api';
import { ProductDocumentForm } from './ProductDocumentForm';

interface ProductsDocumentHistoryProps {
  productId: string;
  onDocumentClick?: (document: ProductDocumentDto) => void;
  isLoading?: boolean;
}

export function ProductsDocumentHistory({
  productId,
  onDocumentClick,
}: ProductsDocumentHistoryProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [archivedDocuments, setArchivedDocuments] = useState<ProductDocumentDto[]>([])
  const [activeDocuments, setActiveDocuments] = useState<ProductDocumentDto[]>([])
  const { openPanel } = usePanel()

  useEffect(() => {
    const getProductDocuments = async () => {
      try {
        const response = await documentsAPI.getProductDocuments({ productId })
        if (response.archivedDocuments) {
          setArchivedDocuments(response.archivedDocuments)
        }
        if (response.activeDocuments) {
          setActiveDocuments(response.activeDocuments)
        }
      } catch (error) {

      } finally {

      }
    }

    getProductDocuments()
  }, [productId])

  const columns: CardTableColumn<ProductDocumentDto>[] = [
    {
      key: 'label',
      label: 'Document',
      width: 'col-span-6',
      render: (doc) => (
        <div className="flex items-start justify-center flex-col">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDocumentClick?.(doc);
              }}
              className="text-primary font-semibold hover:text-primary hover:underline transition-colors"
              disabled={!doc.storedFileId}
            >
              {doc.label}
            </button>
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
              v{doc.version}
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {doc.isGroup ? 'Groupe' : 'Individuel'}
          </p>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      width: 'col-span-3',
      render: (doc) => (
        doc.isGroup ? (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
          >
            Groupe
          </span>
        ) : (
          <span className="text-sm text-gray-400">Individuel</span>
        )
      )
    },
    {
      key: 'creationDate',
      label: 'Date de création',
      width: 'col-span-3',
      render: (doc) => (
        <span className="text-sm text-gray-600">
          {new Date(doc.creationDate).toLocaleDateString('fr-FR')}
        </span>
      )
    }
  ];

  const openAddForm = useCallback(() => {
    openPanel({
      title: 'Ajouter un document produit',
      size: Sizes['4xl']
    },
      <ProductDocumentForm productId={productId} />
    )
  }, [openPanel, productId])

  const titleAside = (
    <Button
      color='primary'
      round
      onClick={openAddForm}
    >
      <Plus className="w-4 h-4" />
      Nouveau document
    </Button>
  );

  return (
    <div className='space-y-4'>
      <CardTableDisplay
        title="Documents actifs"
        icon={FileText}
        iconColor="text-primary"
        titleAside={titleAside}
        columns={columns}
        data={activeDocuments}
        onItemClick={onDocumentClick}
        emptyStateMessage="Aucun document disponible"
        isLoading={isLoading}
      />
      <CardTableDisplay
        title="Historique des documents"
        icon={Clock}
        iconColor="text-primary"
        columns={columns}
        data={archivedDocuments}
        emptyStateMessage="Aucun document disponible"
        isLoading={isLoading}
      />
    </div>
  );
}
